import { format } from '@tusbar/cache-control'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { QuickBuyModal } from '../../../brunswick/components/QuickBuyModal'
import {
  CONTENT_KEY,
  INCLUDE_UNAVAILABLE_PRODUCTS,
  PREVIEW,
  PRODUCT_DATA_KEY,
} from '../../../brunswick/constants'
import { QuickBuyContainer } from '../../../brunswick/containers/QuickBuyContainer'
import { MagnoliaPage, MagnoliaPageInitialProps } from '../../../components/pages/MagnoliaApp'
import {
  primeClientForFetchProductPageData,
  useFetchProductPageData,
} from '../../../components/pages/products/ProductPage/hooks/useFetchProductPageData'
import { ProductPage } from '../../../components/pages/products/ProductPage/ProductPage'
import ProductPageMeta from '../../../components/pages/products/ProductPage/ProductPageMeta'
import { TEMPORARY_REDIRECT } from '../../../constants'
import { cachePolicies } from '../../../lib/response-cache'
import { isBrowser } from '../../../lib/utils'
import { singleQueryParamValue } from '../../../lib/utils'

export type ProductPageRouteHandlerProps = MagnoliaPageInitialProps<{
  productGroupHandle: string
  includeUnavailableProducts: boolean
  previewUnpublishedContent: boolean
  launchTags: string | undefined
  color: string | undefined
  fit: string | undefined
}>

const ProductPageRouteHandler: MagnoliaPage<ProductPageRouteHandlerProps> = ({
  productGroupHandle,
  includeUnavailableProducts,
  previewUnpublishedContent,
  launchTags,
  color: ssrColor,
  fit: ssrFit,
}) => {
  // Read URL again on the client, use server found data as the default.
  // We need to do this so changes to the route cause a rerender at this level
  // and new data will be retrieved based on the client side route change.
  //
  // There appears to be an issue with `getInitialProps` page handlers and using `useQuery` in this component.
  // `useQuery` will return incorrect data if this component rerenders due to `useFetchProductPageData` during
  // the route transition. For now, using vanilla JS checks for these query params. This should not be an issue
  // in the app directory. Also probably would be fine if we were using `getServerSideProps`

  const clientSearchParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined

  const color = clientSearchParams ? clientSearchParams.get('color') ?? undefined : ssrColor
  const fit = clientSearchParams ? clientSearchParams.get('fit') ?? undefined : ssrFit

  const productPageData = useFetchProductPageData({
    productGroupHandle,
    featuredProductColor: color,
    featuredProductFit: fit,
    isPreview: previewUnpublishedContent,
    includeUnavailable: includeUnavailableProducts,
    launchTags,
  })

  /**
   * Split up data needed for the SEO and Page components.
   */
  const { description, images, noindex, seoDescription, seoTitle, title, videos } =
    productPageData.featuredProduct ?? {}
  const image = images?.nodes[0]?.source
  const video = videos?.nodes[0]?.source

  return (
    <QuickBuyContainer.Provider>
      <ProductPageMeta
        description={description}
        image={image}
        noindex={noindex || includeUnavailableProducts || previewUnpublishedContent}
        seoDescription={seoDescription}
        seoTitle={seoTitle}
        title={title}
        video={video}
        handle={productPageData.productGroup?.handle ?? productGroupHandle}
        colorName={productPageData.featuredProduct?.color}
        colors={productPageData.productGroup?.colors ?? []}
      />

      <ProductPage {...productPageData} />
      <QuickBuyModal />
    </QuickBuyContainer.Provider>
  )
}

ProductPageRouteHandler.getInitialProps = async function (pageContext) {
  /**
   * Read data from the request
   */
  const productDataParams = singleQueryParamValue(pageContext.query[PRODUCT_DATA_KEY], '')
  const includeUnavailableProducts = productDataParams === INCLUDE_UNAVAILABLE_PRODUCTS
  const launchTags = includeUnavailableProducts ? '' : productDataParams
  const previewUnpublishedContent = pageContext.query[CONTENT_KEY] === PREVIEW
  // color and fit must default to undefined to avoid passing an empty string to the featured product query:
  const color = singleQueryParamValue(pageContext.query.color, undefined)
  const fit = singleQueryParamValue(pageContext.query.fit, undefined)
  const productGroupHandle = singleQueryParamValue(pageContext.query.handle, '')
  const cacheBypass =
    singleQueryParamValue(pageContext.query.features, '') === process.env.CACHE_BYPASS_FEATURE
  const explicitNoCache =
    includeUnavailableProducts || previewUnpublishedContent || cacheBypass || !!launchTags?.length

  const pageProps: ProductPageRouteHandlerProps = {
    productGroupHandle,
    includeUnavailableProducts,
    previewUnpublishedContent,
    launchTags,
    responseCacheControl: cachePolicies.noCache,
    color,
    fit,
  }

  /**
   * Render the page immediately by not blocking on getInitialProps and let the
   * page component run a fresh query client-side:
   */
  if (isBrowser()) return pageProps

  /**
   * Prime the Apollo Client cache for SSR
   */
  const client = pageContext.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)
  const [productGroupQueryPromise, featuredProductQueryPromise] =
    primeClientForFetchProductPageData({
      client,
      props: {
        productGroupHandle,
        featuredProductColor: color,
        featuredProductFit: fit,
        isPreview: previewUnpublishedContent,
        includeUnavailable: includeUnavailableProducts,
        launchTags,
      },
    })

  const {
    data: { productGroup },
    errors: productGroupErrors,
  } = await productGroupQueryPromise

  const {
    data: { productGroup: featuredProductProductGroup },
    errors: featuredProductErrors,
  } = await featuredProductQueryPromise

  /**
   * Combine any errors
   */
  const graphQLErrors: ReadonlyArray<Error> = [
    ...(productGroupErrors ?? []),
    ...(featuredProductErrors ?? []),
  ]

  /**
   * Handle setting http response codes other than 200
   */
  // Redirect if there was a requested color or fit, but we didn't find a featured product for them
  if (
    pageContext.res &&
    productGroup &&
    !featuredProductProductGroup?.featuredProduct &&
    (color || fit)
  ) {
    pageContext.res.writeHead(TEMPORARY_REDIRECT, {
      'Cache-Control': format(cachePolicies.publicHtmlPolicy),
      'Location': pageContext.pathname.replace('[handle]', productGroupHandle),
    })
    pageContext.res.end()
    return pageProps
  }

  // 404 if we didn't find a productGroup or a featuredProduct
  if (
    pageContext.res &&
    (!productGroup ||
      !featuredProductProductGroup?.featuredProduct ||
      productGroup.productsForAvailability.length === 0)
  ) {
    pageContext.res.statusCode = 404
  }

  return {
    ...pageProps,
    responseCacheControl:
      explicitNoCache || graphQLErrors.length > 0
        ? cachePolicies.noCache
        : cachePolicies.publicHtmlPolicy,
  }
}

export default ProductPageRouteHandler
